import { Link } from 'react-router-dom';
import { ArrowRight, RefreshCw, Sparkles } from 'lucide-react';

import { SectionHeader } from '@/components/ui/section-header';
import { Skeleton } from '@/components/ui/skeleton';
import { SoftCard } from '@/components/ui/soft-card';
import { useShop } from '@/hooks/useShop';
import { useShopPath } from '@/hooks/useShopPath';
import { useInventoryPageSummary } from '@/hooks/useInventory';

import { ConnectionStatusCard } from './components/ConnectionStatusCard';
import { FeedIssues } from './components/FeedIssues';
import { InvisibleProductsCard } from './components/InvisibleProductsCard';
import { ProfileCompletenessBar } from './components/ProfileCompletenessBar';
import { VisibilityHeaderCard } from './components/VisibilityHeaderCard';
import { deriveConnectionState } from '@/lib/connection-state';
import { PageScore } from '@/components/overview/LocalPotentialHeader';
import { deriveHeroState } from './lib/derive-hero-state';
import { awaitingUploadCopy } from '@/lib/storefront-launch';

function InventoryLoadingSkeleton() {
	return (
		<div className="space-y-10">
			<Skeleton className="h-32 w-full rounded-2xl" />
			<div className="space-y-6">
				<div className="flex items-end justify-between gap-4">
					<div className="space-y-2">
						<Skeleton className="h-3 w-32" />
						<Skeleton className="h-6 w-72" />
						<Skeleton className="h-4 w-96" />
					</div>
					<Skeleton className="h-10 w-44 rounded-full" />
				</div>
				<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
					<Skeleton className="h-48 rounded-2xl" />
					<Skeleton className="h-48 rounded-2xl" />
				</div>
				<div className="pt-4">
					<div className="space-y-2 pb-4">
						<Skeleton className="h-3 w-44" />
						<Skeleton className="h-6 w-80" />
					</div>
					<div className="space-y-4">
						<Skeleton className="h-12 w-full rounded-xl" />
						<Skeleton className="h-12 w-full rounded-xl" />
						<Skeleton className="h-12 w-full rounded-xl" />
					</div>
				</div>
			</div>
		</div>
	);
}

export function Inventory() {
	const { shop } = useShop();
	const { shopPath } = useShopPath();
	const { summary, isLoading } = useInventoryPageSummary(shop?.id);
	// We can already tell whether the shop has a POS connected from `useShop`
	// (synchronous), so skeleton the catalogue cards only when we expect them
	// to populate. A shop with no POS goes straight to the "Connect a POS"
	// copy below — no point showing a skeleton that resolves to an empty
	// state.
	const shopReady = !!shop;
	const expectingSummary = shopReady && !!shop?.stockInventorySource;
	const summaryLoading = expectingSummary && isLoading && !summary;

	const { source, sourceLabel, hasSource, firstSyncInProgress, stalledNoCredentials, noSource, showCatalogue } = deriveHeroState({
		shop,
		summary,
		shopReady,
		summaryLoading
	});
	const inStockItems = summary?.inStockItems ?? null;
	const totalItems = summary?.totalItems ?? null;
	// Show the priority-fix card whenever any profile attribute has a gap —
	// the card rotates through title → image → description → brand → category
	// to surface the next outstanding fix. When everything is complete, the
	// Visible-to-shoppers card takes the full row.
	const profileCompleteness = summary?.profileCompleteness ?? [];
	const hasPriorityFix = profileCompleteness.some((s) => (s.missingCount ?? 0) > 0);
	// A frozen feed (≥14d without an ingest) flips the visibility card into its
	// data-outdated state instead of claiming stale counts (NEA-5934).
	const connectionState = deriveConnectionState({ source });
	const staleDays = connectionState.needsReconnect ? connectionState.daysSinceSync : null;

	return (
		<div className="space-y-10">
			<header className="flex flex-wrap items-start justify-between gap-4">
				<div>
					<h1 className="text-3xl font-extrabold tracking-tight md:text-[32px]">Inventory</h1>
					<p className="mt-2 text-muted-foreground">
						{summaryLoading
							? 'Loading your inventory…'
							: firstSyncInProgress
								? `We're pulling your products in${sourceLabel ? ` from ${sourceLabel}` : ''} right now — your catalogue is taking shape.`
								: !stalledNoCredentials && sourceLabel
									? `Your stock, synced from ${sourceLabel} — and how it looks across your channels.`
									: 'Connect a POS to start syncing your stock.'}
					</p>
				</div>
				<div className="ml-auto shrink-0">
					<PageScore />
				</div>
			</header>

			{summaryLoading ? <InventoryLoadingSkeleton /> : null}

			{!summaryLoading && firstSyncInProgress ? (
				<SoftCard tone="soft" className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
					<div className="flex items-center gap-4">
						<div className="flex h-11 w-11 items-center justify-center rounded-full bg-success-soft">
							<RefreshCw className="h-6 w-6 animate-spin text-success-soft-foreground" />
						</div>
						<div>
							<div className="font-semibold">First sync in progress{sourceLabel ? ` · ${sourceLabel}` : ''}</div>
							<div className="text-sm text-muted-foreground">
								{awaitingUploadCopy({ sourceLabel, connectType: source?.connectType })}
							</div>
						</div>
					</div>
				</SoftCard>
			) : null}

			{!summaryLoading && firstSyncInProgress ? (
				<SoftCard tone="white" className="flex flex-col gap-4 border-primary/20 md:flex-row md:items-center md:justify-between">
					<div className="flex items-start gap-4">
						<div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10">
							<Sparkles className="h-5 w-5 text-primary" />
						</div>
						<div>
							<div className="font-semibold">Your Local Storefront unlocks after this first sync</div>
							<p className="mt-0.5 text-sm text-muted-foreground">
								{/* The launch is gated on the first upload landing (NEA-6534) — no point
								    sending them to a button they can't press yet. */}
								As soon as your products arrive you'll be able to launch your Local Storefront and start showing them to
								local shoppers.
							</p>
						</div>
					</div>
					<Link
						to={shopPath('/channels/storefront')}
						className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border bg-white px-4 py-2 text-sm font-semibold text-foreground transition hover:bg-muted"
					>
						About Local Storefront <ArrowRight className="h-3.5 w-3.5" />
					</Link>
				</SoftCard>
			) : null}

			{!summaryLoading && noSource ? <ConnectionStatusCard source={null} hasEverIngested={!!shop?.hasEverIngested} /> : null}

			{showCatalogue ? (
				<div className="space-y-10">
					<ConnectionStatusCard source={source} totalItems={totalItems} inStockItems={inStockItems} />

					<div className="space-y-6">
						<SectionHeader
							eyebrow="Product data"
							title="How your products look to locals"
							sub="The richer your data, the more of your catalogue every channel will show."
							rightSlot={
								<Link
									to={shopPath('/inventory/manage')}
									className="mt-[22px] inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
								>
									Manage products <ArrowRight className="h-3.5 w-3.5" />
								</Link>
							}
						/>
						{hasPriorityFix ? (
							<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
								<VisibilityHeaderCard
									visibility={summary?.visibility}
									inStockItems={inStockItems ?? 0}
									gmcProductStats={summary?.gmcProductStats}
									staleDays={staleDays}
								/>
								<InvisibleProductsCard profileCompleteness={profileCompleteness} inStockItems={inStockItems ?? 0} />
							</div>
						) : (
							<VisibilityHeaderCard
								visibility={summary?.visibility}
								inStockItems={inStockItems ?? 0}
								gmcProductStats={summary?.gmcProductStats}
								staleDays={staleDays}
							/>
						)}
						<div className="pt-4">
							<SectionHeader
								eyebrow="Improve your profiles"
								title="Richer profiles reach more local shoppers"
								sub="Complete more fields to increase your chances of winning local shoppers."
							/>
							<ProfileCompletenessBar profileCompleteness={summary?.profileCompleteness ?? []} />
						</div>
						<FeedIssues shopId={shop?.id} />
					</div>
				</div>
			) : null}
		</div>
	);
}
